import { removeAccents } from '../../../../../../utils';
import { ImportKind } from './ImportKindToggle';

/** Rejets renvoyés par l'API pour les lignes qu'elle n'a pas pu traiter. */
export type ImportRejection = {
  data: Record<string, unknown>;
  errors: Record<string, string[]>;
  /** Rang de la ligne parmi les lignes de données, hors en-tête (1 = première ligne). */
  _row_number?: number;
};

export type ImportSummary = {
  total_rows: number;
  valid_count: number;
  invalid_count: number;
  duplicate_count: number;
  saved_count: number;
};

/**
 * Entrée de `valid_data`. L'API y mélange deux formes : un point de service créé, qui porte son
 * `id`, et un doublon ignoré, qui reprend les données validées de la ligne et porte
 * `duplicate: true` sans `id`. Les clés varient avec le type d'import, d'où la signature d'index.
 */
export type ImportValidEntry = Record<string, unknown> & { id?: number; duplicate?: boolean };

export type ImportResult = {
  summary: ImportSummary;
  invalid_data?: ImportRejection[];
  valid_data?: ImportValidEntry[];
};

/**
 * Identifiants des points de service réellement créés par l'import.
 *
 * Le doublon est écarté sur `duplicate` autant que sur l'absence d'`id` : il n'en porte pas
 * aujourd'hui, mais le voir un jour sérialisé avec celui de la demande existante ne doit pas le
 * faire passer pour une création.
 */
export function savedRequestIds(result: ImportResult): number[] {
  return (result.valid_data ?? []).flatMap((entry) =>
    entry.duplicate !== true && typeof entry.id === 'number' ? [entry.id] : []
  );
}

/** Doublons ignorés par l'API, extraits de `valid_data` où ils cohabitent avec les créations. */
export function importDuplicates(result: ImportResult): ImportValidEntry[] {
  return (result.valid_data ?? []).filter((entry) => entry.duplicate === true);
}

/**
 * Doublons remis à la forme d'un rejet, pour les afficher dans la même liste que les lignes
 * refusées à la validation.
 *
 * Le motif arrive déjà traduit : ce module reste hors de la couche i18n. Il part sous
 * `non_field_errors`, la clé que la modale affiche sans la préfixer d'un nom de colonne — un
 * doublon ne désigne aucun champ à corriger. Les données de la ligne sont reprises telles quelles,
 * pour que la modale y retrouve l'identifiant tel que l'utilisateur l'a saisi.
 */
export function duplicateRejections(
  entries: ImportValidEntry[],
  reason: string
): ImportRejection[] {
  return entries.map((entry) => ({ data: entry, errors: { non_field_errors: [reason] } }));
}

/** Endpoints repris de la version précédente. */
const COPY_PASTE_ENDPOINTS: Record<ImportKind, string> = {
  maintenance: '/api/boi/exocet/import-devices-copy-paste-maintenance/',
  create: '/api/boi/exocet/import-devices-copy-paste-create-pds/'
};

const FILE_ENDPOINTS: Record<ImportKind, string> = {
  maintenance: '/api/boi/exocet/import-devices-file-maintenance/',
  create: '/api/boi/exocet/import-devices-file-create-pds/'
};

export const SUPPORTED_IMPORT_EXTENSIONS = ['csv', 'xlsx'] as const;

/** Le fuseau est passé en paramètre plutôt que lu ici, pour rester testable. */
function buildImportUrl(endpoint: string, clientId: number, timezone: string): string {
  const params = new URLSearchParams({
    timezone,
    customer_id: String(clientId)
  });

  return `${endpoint}?${params.toString()}`;
}

export function buildCopyPasteImportUrl(
  kind: ImportKind,
  clientId: number,
  timezone: string
): string {
  return buildImportUrl(COPY_PASTE_ENDPOINTS[kind], clientId, timezone);
}

export function buildFileImportUrl(kind: ImportKind, clientId: number, timezone: string): string {
  return buildImportUrl(FILE_ENDPOINTS[kind], clientId, timezone);
}

/**
 * L'API attend le contenu collé dans un champ `content` de formulaire multipart,
 * comme pour l'import de fichier.
 */
export function buildCopyPasteFormData(pastedText: string): FormData {
  const formData = new FormData();
  formData.append('content', pastedText);

  return formData;
}

/**
 * Les accents du nom de fichier sont retirés avant l'envoi, comme en v1 : le
 * back-end ne les accepte pas dans l'en-tête multipart.
 */
export function buildFileFormData(file: File): FormData {
  const formData = new FormData();
  formData.append('file', file, file.name ? removeAccents(file.name) : 'imported_file');

  return formData;
}

/**
 * Seuls les CSV et XLSX sont acceptés. Contrairement à la v1, la comparaison
 * ignore la casse : un fichier « RELEVE.CSV » était refusé à tort.
 */
export function isSupportedImportFile(file: File): boolean {
  const extension = file.name.split('.').pop()?.toLowerCase();

  return SUPPORTED_IMPORT_EXTENSIONS.some((supported) => supported === extension);
}

const CHERRY_PICK_ENDPOINT = '/api/boi/exocet/import-devices-patrimony-cherrypick/';

export type CherryPickPayload = {
  equipment: string;
  contact_name: string;
  contact_telephone: string;
};

/** La saisie unitaire ne prend pas de type d'ajout : elle pioche dans le patrimoine. */
export function buildCherryPickUrl(clientId: number): string {
  return `${CHERRY_PICK_ENDPOINT}?${new URLSearchParams({ customer_id: String(clientId) })}`;
}

/**
 * Le téléphone part indicatif puis numéro, séparés d'une espace, et reste vide si
 * aucun numéro n'est saisi — format attendu par l'API, repris de la v1.
 */
export function buildCherryPickPayload(
  draft: { equipmentId: string; contactName: string; contactPhone: string },
  dialCode: string
): CherryPickPayload {
  const phone = draft.contactPhone.trim();

  return {
    equipment: draft.equipmentId.trim(),
    contact_name: draft.contactName.trim(),
    contact_telephone: phone ? `${dialCode} ${phone}` : ''
  };
}
